import { METEOAPI_BASE_PATHS } from "@Constants/apiBasePaths";
import { createClient, createConfig } from "@HeyMeteoApi/client";
import { Favorite, getFavorites, replaceFavorite } from "@HeyMeteoApi/index";
import { LoginUser } from "@Hooks/useLogin";
import { useCallback, useEffect, useState } from "react";

export const useFavorites = (env: string, user?: LoginUser) => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchFavorites = async () => {
      const token = user?.idToken;
      setLoading(true);
      if (!token || !env) {
        setFavorites([]);
        setLoading(false);
        return;
      }
      const meteoApiClient = createClient(
        createConfig({
          auth: () => token,
          baseUrl: METEOAPI_BASE_PATHS[env as keyof typeof METEOAPI_BASE_PATHS],
        }),
      );
      try {
        const response = await getFavorites({
          client: meteoApiClient,
        });

        if (response?.error) {
          setError(true);
          setFavorites([]);
          return;
        }
        setFavorites(response.data?.favorites ?? []);
      } catch {
        setError(true);
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [env, user]);

  const moveFavoriteUp = useCallback((index: number) => {
    if (index > 0) {
      setFavorites((prev) => {
        const item = prev[index];
        const newFavorites = prev.filter((_, i) => i !== index);
        newFavorites.splice(index - 1, 0, item);
        return newFavorites;
      });
    }
  }, []);

  const moveFavoriteDown = useCallback((index: number) => {
    setFavorites((prev) => {
      if (index < prev.length - 1) {
        const item = prev[index];
        const newFavorites = prev.filter((_, i) => i !== index);
        newFavorites.splice(index + 1, 0, item);
        return newFavorites;
      }
      return prev;
    });
  }, []);

  const saveFavorites = useCallback(
    async (favoritesToSave?: Favorite[]) => {
      if (!user || !env) {
        return;
      }

      try {
        const token = user.idToken;
        const meteoApiClient = createClient(
          createConfig({
            auth: () => token,
            baseUrl:
              METEOAPI_BASE_PATHS[env as keyof typeof METEOAPI_BASE_PATHS],
          }),
        );

        const favoritesData = {
          updated: new Date().toISOString(),
          favorites: favoritesToSave ?? favorites,
        };

        await replaceFavorite({
          body: favoritesData,
          client: meteoApiClient,
        });
      } catch {
        setError(true);
      }
    },
    [favorites, env, user],
  );

  const addFavorite = useCallback((favorite: Favorite) => {
    setFavorites((prev) => {
      return [...prev, favorite];
    });
  }, []);

  const deleteFavorite = useCallback(
    (index: number) => {
      const updatedFavorites = favorites.filter((_, i) => i !== index);
      setFavorites(updatedFavorites);

      return updatedFavorites;
    },
    [favorites],
  );

  return {
    favorites,
    loading,
    error,
    moveFavoriteUp,
    moveFavoriteDown,
    addFavorite,
    deleteFavorite,
    saveFavorites,
  };
};
